import { useSearchParams } from "react-router-dom";

/**
 * Reads the `?blt={token}` continuation token the /buy landing page hands over.
 *
 * A visitor who fills in the short form on `public/buy.html` has already given
 * Phoenix their email, and a BuyerLead row exists for them. The token is what
 * makes their prequalification COMPLETE that lead instead of creating a second
 * one — the same person arriving twice, once as a lead and once as an
 * application, with nothing joining the two.
 *
 * Deliberately far simpler than `usePrequalInvite`, and the difference is the
 * point: there is nothing to resolve. An invite has a recipient whose name we
 * prefill and a liveness the funnel must not guess at, so it costs a request.
 * This token prefills nothing and gates nothing — the server is the only thing
 * that ever looks at it, at finalize, and it attaches the completion or
 * silently does not. Making the funnel ask about it first would buy no decision
 * anyone here can act on.
 *
 * Failure posture mirrors the invite hook's: never throws, never blocks, never
 * shows the visitor an error. An unknown, expired, spent or malformed token
 * lands the submission as ordinary self-serve traffic. Capturing the lead
 * always wins over rejecting it.
 *
 * ── THE TOKEN STAYS IN THE URL, AND THAT IS A DECISION ───────────────────
 *
 * 🔴 We do NOT `history.replaceState` it away after reading it. Stripping it
 * would look like hygiene and would in fact be a data-loss bug: `blt` is one of
 * the three members of the funnel's session fingerprint
 * (`fingerprintScope` in prequalStorage.ts), so removing it from the address
 * bar mid-run changes the scope under a live mount, and the next mount — a
 * refresh, a back button — reads the applicant's OWN answers as somebody
 * else's and clears them. Cleaning up the URL would cost the visitor the form
 * they are filling in.
 *
 * So the token is visible in browser history, in server access logs, and —
 * since gtag is installed on the SPA shell — in the `page_view` URLs Google
 * receives. That is an accepted risk, not an oversight, and it is survivable
 * ONLY because the token is single-use, expires in 24 hours, and attaches
 * NOTHING without an email that matches the lead it was minted for. A leaked
 * token in a log therefore buys an attacker the ability to attach an
 * application they can only submit by already knowing the victim's email
 * address.
 *
 * 🔴 IF THAT EMAIL CONDITION IS EVER WEAKENED, THIS DECISION HAS TO BE
 * REVISITED. The token would then be a bearer credential sitting in three
 * places we do not control, and neither single use nor a 24-hour window is
 * enough on its own.
 */
export function usePrequalBuyLandingToken(): string | null {
  const [params] = useSearchParams();
  const raw = params.get("blt");
  // Blank normalises to null so the fingerprint and the wire value agree: the
  // scope already collapses "" to null, and without this a `?blt=` with nothing
  // after it would fingerprint as no-token while still sending an empty string
  // to finalize. Harmless, and exactly the sort of disagreement that costs an
  // hour when someone later asks why the scope did not change.
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}
